import { run, state, waitFor } from '@mvf/product-components/sagas';
import {
  changeHistory,
  historyUpdated,
  notOnPage,
  onPage,
} from '@mvf/product-components/historyListener';
import {
  all,
  cancel,
  ChannelTakeEffect,
  delay,
  fork,
  ForkEffect,
  put,
  take,
} from 'redux-saga/effects';
import { EventChannel, eventChannel, Task } from 'redux-saga';
import { context, trace } from '@opentelemetry/api';
import Cookies from 'js-cookie';
import { createNotification } from '@mvf/product-components/messageNotifier';
import { Severity } from '@mvf/product-components/enums';
import { IClientResultError } from 'appwiki-link-shield-proxy';
import { map } from 'lodash-es';
import { Page } from '../Page';
import {
  loadLinkShieldSession,
  setTestsCompleted,
  startRedirect,
  testRecaptcha,
  testCheq,
  ITestProvider,
} from '../../Features/LoadingBar/loadingBarReducer';
import {
  IRunTriggerFn,
  LinkShieldSession,
} from '../../Features/LoadingBar/LoadingBarActionTypes';
import {
  executeRecaptcha,
  loadRecaptchaScript,
  waitForRecaptchaToBeReady,
} from '../../Global/Services/RecaptchaService';
import { traceContext } from '../../Global/Sagas/traceContext';
import { getLinkShieldSession } from '../../Features/LoadingBar/loadingBarSelectors';
import {
  executeCheq,
  loadCheqScript,
  waitForCheqToBeReady,
} from '../../Global/Services/CheqService';
import type { ActionCreatorWithPayload } from '@reduxjs/toolkit';

const tracer = trace.getTracer('features.sagas.loading_bar');

const createMouseExitChannel = () =>
  eventChannel<MouseEvent>((emit) => {
    const handler = (event: MouseEvent) => {
      emit(event);
    };

    document.addEventListener('mousemove', handler);

    return () => document.removeEventListener('mousemove', handler);
  });

function* waitForMouseExitIntent(
  channel: EventChannel<MouseEvent>,
): Generator<ChannelTakeEffect<MouseEvent>, void, MouseEvent> {
  let prevY: number | undefined;
  let prevTime: number | undefined;

  const frameEstimate = 10; // project ~50ms into the future

  while (true) {
    const event = yield take(channel);
    const currY = event.clientY;
    const currTime = event.timeStamp;

    if (prevY && prevTime && currTime > prevTime) {
      const dt = currTime - prevTime;
      const vy = (currY - prevY) / dt;

      const predictedY = currY + vy * frameEstimate;

      if (predictedY <= 0) {
        prevY = undefined;
        prevTime = undefined;
        return;
      }
    }

    prevY = currY;
    prevTime = currTime;
  }
}

export function* navigationingAwaySaga() {
  while (true) {
    const { payload } = yield* waitFor(historyUpdated);
    if (yield* state(notOnPage, Page.HOLDING)) {
      continue;
    }

    const linkShieldSession = yield* state(getLinkShieldSession);
    if (!linkShieldSession) {
      continue;
    }

    const isIncomplete = ['#test', '#bypass'].includes(
      payload.previousLocation.hash,
    );
    const isGoingToDone = ['#done'].includes(payload.location.hash);

    const isDone = ['#done'].includes(payload.previousLocation.hash);
    const isGoingToRedirect = ['#redirect'].includes(payload.location.hash);

    if (isIncomplete && !isGoingToDone) {
      yield put(changeHistory({ hash: 'bypass' }));
      yield put(setTestsCompleted());
    } else if (isDone && !isGoingToRedirect) {
      yield put(changeHistory({ hash: 'done' }));
    } else if (payload.location.hash === '') {
      yield put(changeHistory({ hash: 'test' }));
    }
  }
}

function* forkRedirectToBypass(): Generator<ForkEffect, Task, Task> {
  return yield fork(function* () {
    yield delay(50);
    yield put(changeHistory({ hash: 'bypass' }));
  });
}

export function* mouseExitIntentSaga() {
  while (true) {
    yield* waitFor(historyUpdated);
    if (yield* state(onPage, Page.HOLDING)) {
      break;
    }
  }

  const mouseEventsChannel = yield* run(createMouseExitChannel);

  let task: Task | undefined;

  while (true) {
    yield* waitForMouseExitIntent(mouseEventsChannel);
    if (task) {
      yield cancel(task);
    }

    task = yield* forkRedirectToBypass();
  }
}

const makeRunTestAction =
  (
    action: ActionCreatorWithPayload<
      { provider: ITestProvider<IRunTriggerFn> },
      string
    >,
  ) =>
  (errorOrFn: IClientResultError | IRunTriggerFn) =>
    action({
      provider: {
        status: 'LOADED',
        instance:
          typeof errorOrFn === 'function' ? errorOrFn : async () => errorOrFn,
      },
    });

function* loadRecaptchaSaga() {
  const activeContext = yield* traceContext();
  const span = tracer.startSpan('recaptcha.load', {}, activeContext);

  try {
    const buildResultFrom = makeRunTestAction(testRecaptcha);

    const script = yield* run(context.bind(activeContext, loadRecaptchaScript));
    if (script.status === 'ERROR') {
      yield put(buildResultFrom(script));
      return;
    }

    const recaptcha = yield* run(
      context.bind(activeContext, waitForRecaptchaToBeReady),
    );
    if (recaptcha.status === 'ERROR') {
      yield put(buildResultFrom(recaptcha));
      return;
    }

    yield put(buildResultFrom(executeRecaptcha(recaptcha.instance)));
  } finally {
    span.end();
  }
}

function* loadCheqSaga() {
  const activeContext = yield* traceContext();
  const span = tracer.startSpan('cheq.load', {}, activeContext);

  try {
    const buildResultFrom = makeRunTestAction(
      testCheq as ActionCreatorWithPayload<
        { provider: ITestProvider<IRunTriggerFn> },
        string
      >,
    );

    const script = yield* run(context.bind(activeContext, loadCheqScript));
    if (script.status === 'ERROR') {
      yield put(buildResultFrom(script));
      return;
    }

    const cheq = yield* run(context.bind(activeContext, waitForCheqToBeReady));
    if (cheq.status === 'ERROR') {
      yield put(buildResultFrom(cheq));
      return;
    }

    yield put(buildResultFrom(executeCheq(cheq.instance)));
  } finally {
    span.end();
  }
}

function* loadProvidersSaga() {
  const result = LinkShieldSession.safeParse(Cookies.get('linkShieldSession'));
  if (!result.success) {
    yield put(
      createNotification({
        message: 'Invalid link shield session',
        severity: Severity.ERROR,
      }),
    );
    return;
  }

  yield put(loadLinkShieldSession(result.data));

  while (true) {
    yield* waitFor(historyUpdated);
    if (yield* state(onPage, Page.HOLDING)) {
      break;
    }
  }

  const providerLoaders = {
    RECAPTCHA: loadRecaptchaSaga,
    CHEQ: loadCheqSaga,
  } as const;

  const activeLoaders = map(result.data.activeTests, ({ type }) =>
    providerLoaders[type](),
  );

  yield all(activeLoaders);
}

function* detectActiveTestsDoneSaga() {
  while (true) {
    yield* waitFor(setTestsCompleted);
    yield put(changeHistory({ hash: 'done' }));
  }
}

function* redirectionSaga() {
  while (true) {
    const { payload } = yield* waitFor(startRedirect);

    window.history.replaceState(null, '', location.href);
    location.replace(payload.redirect);
    yield put(changeHistory({ hash: 'redirect' }));
  }
}

export function* holdingPageSagas() {
  yield all([
    loadProvidersSaga(),

    navigationingAwaySaga(),
    mouseExitIntentSaga(),

    detectActiveTestsDoneSaga(),
    redirectionSaga(),
  ]);
}
